import * as React from 'react';

import {
  BodySmall,
  BodyMediumBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {SimpleButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {ChartWrapper} from '@spaced-out/ui-design-system/lib/components/Charts/ChartWrapper';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {classify} from 'src/utils/classify';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDashboardStore} from 'src/components/analytics-genesis/byod/store/dashboardStore';
import {useTileData} from 'src/components/analytics-genesis/byod/api';
import {ByodKpiBox} from 'src/components/analytics-genesis/byod/components/byod-kpi-box';
import {ByodTable} from 'src/components/analytics-genesis/byod/components/byod-table';
import {ByodChart} from 'src/components/analytics-genesis/byod/components/byod-chart';
import {ByodErrorBoundary} from 'src/components/analytics-genesis/byod/components/byod-error-boundary';
import type {WidgetConfig, ChartStyle} from 'src/types/byod';
import {WIDGET_TYPE} from 'src/types/byod';

import kpiCss from './dashboard-builder-kpi-tile.css';
import vizCss from './dashboard-builder-viz-tile.css';


const CHART_TILE_HEIGHT = 430;

export type CanvasTileProps = {
  tileId: string;
  canEdit: boolean;
  isRemoving: boolean;
  onEdit: (tileId: string) => void;
  onRemove: (tileId: string) => void;
};

export const CanvasTile = React.memo((props: CanvasTileProps) => {
  const widget = useDashboardStore((s) => s.widgets[props.tileId]);
  const wt = widget?.widget_type;
  if (!wt) {
    return null;
  }
  const tileKind = wt === WIDGET_TYPE.KPI ? 'kpi' : 'viz';
  return (
    <ByodErrorBoundary
      variant="tile"
      tileKind={tileKind}
      resetKey={props.tileId}
    >
      <CanvasTileRouter {...props} widget={widget} wt={wt} />
    </ByodErrorBoundary>
  );
});

type InternalTileProps = CanvasTileProps & {
  widget: WidgetConfig;
};

function CanvasTileRouter(
  props: InternalTileProps & {
    wt: NonNullable<WidgetConfig>['widget_type'];
  },
) {
  if (props.wt === WIDGET_TYPE.KPI) {
    return <KpiCanvasTile {...props} />;
  }
  if (props.wt === WIDGET_TYPE.TABLE) {
    return <TableCanvasTile {...props} />;
  }
  return <ChartCanvasTile {...props} />;
}

function TileErrorRetry({
  message,
  onRetry,
  testIdPrefix,
}: {
  message: string;
  onRetry: () => void;
  testIdPrefix: string;
}) {
  const {t} = useLabelTranslation();
  return (
    <div
      className={vizCss.tileErrorRetry}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <BodySmall color="danger">{message}</BodySmall>
      <Button
        type="ghost"
        size="small"
        iconLeftName="arrow-rotate-right"
        iconLeftType="regular"
        onClick={onRetry}
        testId={`${testIdPrefix}-retry`}
      >
        {t('Retry', 'Retry')}
      </Button>
    </div>
  );
}

function KpiTileEllipsisMenu({
  tileId,
  label,
  canEdit,
  isRemoving,
  onEdit,
  onRemove,
}: Pick<
  CanvasTileProps,
  'tileId' | 'canEdit' | 'isRemoving' | 'onEdit' | 'onRemove'
> & {label: string}) {
  const {t} = useLabelTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);

  if (!canEdit) {
    return null;
  }

  const forceVisible = menuOpen || isRemoving;

  return (
    <div
      className={classify(
        kpiCss.kpiTileEllipsisWrap,
        forceVisible && kpiCss.kpiTileEllipsisWrapVisible,
      )}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {!isRemoving && (
        <SimpleButtonDropdown
          type="ghost"
          size="small"
          iconLeftName="ellipsis-v"
          ariaLabel={t('Widget_actions_for', {
            name: label,
            defaultValue: `Widget actions for ${label}`,
          })}
          options={[
            {
              key: 'edit',
              label: t('Edit_widget', 'Edit widget'),
              iconLeft: 'pencil',
            },
            {
              key: 'remove',
              label: t('Remove_widget', 'Remove widget'),
              iconLeft: 'trash',
            },
          ]}
          onOptionSelect={(opt) => {
            if (opt.key === 'edit') {
              onEdit(tileId);
            } else if (opt.key === 'remove') {
              void onRemove(tileId);
            }
          }}
          onMenuOpen={() => setMenuOpen(true)}
          onMenuClose={() => setMenuOpen(false)}
          isFluid={false}
        />
      )}
    </div>
  );
}

export const KpiCanvasTile = React.memo(
  ({
    tileId,
    widget,
    canEdit,
    isRemoving,
    onEdit,
    onRemove,
  }: InternalTileProps) => {
    const {t} = useLabelTranslation();
    const label = widget?.label ?? tileId;

    const {ref, data, isError, isFetching, error, refetch, fetchStatus} =
      useTileData(tileId);

    if (fetchStatus === 'idle' && !data && !isError) {
      return (
        <div
          ref={ref}
          className={kpiCss.kpiTile}
          data-tile-id={tileId}
          data-testid={`byod-kpi-tile-${tileId}-skeleton`}
        />
      );
    }

    const naText = t('NA', 'N/A');

    if (isError) {
      return (
        <div
          ref={ref}
          className={kpiCss.kpiTile}
          data-tile-id={tileId}
          data-testid={`byod-kpi-tile-${tileId}`}
        >
          {canEdit ? (
            <div className={kpiCss.kpiTileMenuRow}>
              <KpiTileEllipsisMenu
                tileId={tileId}
                label={label}
                canEdit={canEdit}
                isRemoving={isRemoving}
                onEdit={onEdit}
                onRemove={onRemove}
              />
            </div>
          ) : null}
          <div className={kpiCss.kpiTileError}>
            <BodySmall color="danger" className={kpiCss.kpiTileErrorMessage}>
              {error?.message ?? naText}
            </BodySmall>
            {/* Loader while retrying — `isError` stays true during the
                refetch, so without the swap the click has no feedback. */}
            {isFetching ? (
              <CircularLoader size="small" />
            ) : (
              <Button
                type="ghost"
                size="small"
                iconLeftName="arrow-rotate-right"
                iconLeftType="regular"
                onClick={refetch}
                testId={`byod-kpi-tile-${tileId}-retry`}
              >
                {t('Retry', 'Retry')}
              </Button>
            )}
          </div>
        </div>
      );
    }

    const middleContent: React.ReactNode =
      data && data.widget_type === WIDGET_TYPE.KPI && data.value != null
        ? String(data.value)
        : naText;

    // Show the shimmer not just on the first load but on any in-flight
    // refetch (date range / filter / config change). With keepPreviousData
    // the hook hands back stale data while the new query is in flight, so
    // without this gate the KPI sits silently on the old value with no
    // indication anything is happening.
    const isLoading = (!data && !isError) || isFetching;

    return (
      <div
        ref={ref}
        className={kpiCss.kpiTile}
        data-tile-id={tileId}
        data-testid={`byod-kpi-tile-${tileId}`}
      >
        {canEdit ? (
          <div className={kpiCss.kpiTileMenuRow}>
            <KpiTileEllipsisMenu
              tileId={tileId}
              label={label}
              canEdit={canEdit}
              isRemoving={isRemoving}
              onEdit={onEdit}
              onRemove={onRemove}
            />
          </div>
        ) : null}
        <ByodKpiBox
          label={label}
          middleContent={middleContent}
          semantic="neutral"
          widgetIcon={widget?.widget_icon}
          kpiboxWrapperClassName={kpiCss.kpiTileKPIBox}
          isLoading={isLoading}
        />
      </div>
    );
  },
);

export const TableCanvasTile = React.memo(
  ({
    tileId,
    widget,
    canEdit,
    isRemoving,
    onEdit,
    onRemove,
  }: InternalTileProps) => {
    const {t} = useLabelTranslation();
    const label = widget?.label ?? tileId;

    const {ref, data, isFetching, isError, error, refetch, fetchStatus} =
      useTileData(tileId);

    const activeFilterCount = React.useMemo(
      () =>
        Object.values(widget?.widget_filters_json ?? {}).filter((v) =>
          Array.isArray(v) ? v.length > 0 : Boolean(v),
        ).length,
      [widget?.widget_filters_json],
    );

    const isTableData = data?.widget_type === WIDGET_TYPE.TABLE;
    const tableColumns = isTableData ? data.columns : [];
    const tableRows = isTableData ? data.rows : [];
    const tableTotalCount = isTableData
      ? data.pagination?.total_count ?? data.rows.length
      : 0;

    const selectedColumns =
      (widget?.widget_config_json?.selected_columns as string[] | undefined) ??
      null;

    const initialSort = React.useMemo(
      () => ({
        sortBy: (widget?.widget_config_json?.sort_by as string) ?? '',
        sortOrder:
          (widget?.widget_config_json?.sort_direction as
            | 'asc'
            | 'desc'
            | 'original') || 'original',
      }),
      [widget?.widget_config_json],
    );

    const storeUpdateWidget = useDashboardStore((s) => s.updateWidget);

    const persistWidgetConfigPatch = React.useCallback(
      (patch: Record<string, unknown>) => {
        const current = useDashboardStore.getState().widgets[tileId];
        if (!current) {
          return;
        }
        storeUpdateWidget(tileId, {
          widget_config_json: {...current.widget_config_json, ...patch},
        });
      },
      [tileId, storeUpdateWidget],
    );

    const handleColumnsChange = React.useCallback(
      (cols: string[]) => {
        persistWidgetConfigPatch({selected_columns: cols});
      },
      [persistWidgetConfigPatch],
    );

    const handleFetchData = React.useCallback(
      (params: {
        offset: number;
        limit: number;
        sort_by?: string;
        sort_direction?: 'asc' | 'desc';
      }) => {
        const stored =
          useDashboardStore.getState().widgets[tileId]?.widget_config_json;
        const patch: Record<string, unknown> = {
          limit: params.limit,
          offset: params.offset,
          sort_by: params.sort_by,
          sort_direction: params.sort_direction,
        };
        const changed = Object.entries(patch).some(
          ([k, v]) => stored?.[k] !== v,
        );
        if (changed) {
          persistWidgetConfigPatch(patch);
        }
      },
      [tileId, persistWidgetConfigPatch],
    );

    if (fetchStatus === 'idle' && !data && !isError) {
      return (
        <div
          ref={ref}
          className={classify(vizCss.tile, vizCss.tableTile)}
          data-tile-id={tileId}
          data-testid={`byod-table-tile-${tileId}-skeleton`}
        />
      );
    }

    if (isError) {
      return (
        <div
          ref={ref}
          className={classify(vizCss.tile, vizCss.tableTile)}
          data-tile-id={tileId}
          data-testid={`byod-table-tile-${tileId}`}
        >
          <div className={vizCss.tileHeader}>
            <div className={vizCss.tileMeta}>
              <BodyMediumBold>{label}</BodyMediumBold>
            </div>
            {canEdit && !isRemoving && (
              <span
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <SimpleButtonDropdown
                  type="ghost"
                  size="small"
                  iconLeftName="ellipsis-v"
                  ariaLabel={t('Widget_actions_for', {
                    name: label,
                    defaultValue: `Widget actions for ${label}`,
                  })}
                  options={[
                    {
                      key: 'edit',
                      label: t('Edit_widget', 'Edit widget'),
                      iconLeft: 'pencil',
                    },
                    {
                      key: 'remove',
                      label: t('Remove_widget', 'Remove widget'),
                      iconLeft: 'trash',
                    },
                  ]}
                  onOptionSelect={(opt) => {
                    if (opt.key === 'edit') {
                      onEdit(tileId);
                    } else if (opt.key === 'remove') {
                      void onRemove(tileId);
                    }
                  }}
                  isFluid={false}
                  testId={`byod-tile-actions-${tileId}`}
                />
              </span>
            )}
          </div>
          {isFetching ? (
            <div className={vizCss.tileLoading}>
              <CircularLoader size="small" />
            </div>
          ) : (
            <TileErrorRetry
              message={error?.message ?? ''}
              onRetry={refetch}
              testIdPrefix={`byod-table-tile-${tileId}`}
            />
          )}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={vizCss.tableTile}
        data-tile-id={tileId}
        data-testid={`byod-table-tile-${tileId}`}
      >
        <ByodTable
          label={label}
          columns={tableColumns}
          rows={tableRows}
          totalCount={tableTotalCount}
          selectedColumns={selectedColumns}
          onSelectedColumnsChange={canEdit ? handleColumnsChange : undefined}
          initialSort={initialSort}
          onFetchData={handleFetchData}
          isLoading={!data || isFetching}
          activeFilterCount={activeFilterCount}
          onEdit={canEdit ? () => onEdit(tileId) : undefined}
          onRemove={canEdit ? () => onRemove(tileId) : undefined}
          isRemoving={isRemoving}
        />
      </div>
    );
  },
);

export const ChartCanvasTile = React.memo(
  ({
    tileId,
    widget,
    canEdit,
    isRemoving,
    onEdit,
    onRemove,
  }: InternalTileProps) => {
    const {t, language} = useLabelTranslation();
    const label = widget?.label ?? tileId;
    const chartStyle =
      (widget?.widget_config_json?.chart_style as ChartStyle | undefined) ??
      undefined;

    const {ref, data, isFetching, isError, error, refetch, fetchStatus} =
      useTileData(tileId);

    const storeUpdateWidget = useDashboardStore((s) => s.updateWidget);

    const handleChartStyleChange = React.useCallback(
      (kind: ChartStyle) => {
        const current = useDashboardStore.getState().widgets[tileId];
        if (!current) {
          return;
        }
        storeUpdateWidget(tileId, {
          widget_config_json: {
            ...current.widget_config_json,
            chart_style: kind,
          },
        });
      },
      [tileId, storeUpdateWidget],
    );

    const menuActions = React.useMemo(() => {
      if (!canEdit) {
        return undefined;
      }
      if (isRemoving) {
        return <CircularLoader size="small" />;
      }
      const options = [
        {
          key: 'edit',
          label: t('Edit_widget', 'Edit widget'),
          iconLeft: 'pencil',
        },
        {
          key: 'remove',
          label: t('Remove_widget', 'Remove widget'),
          iconLeft: 'trash',
        },
      ];
      return (
        <span
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <SimpleButtonDropdown
            type="ghost"
            size="small"
            iconLeftName="ellipsis-v"
            ariaLabel={t('Widget_actions_for', {
              name: label,
              defaultValue: `Widget actions for ${label}`,
            })}
            options={options}
            onOptionSelect={(opt) => {
              if (opt.key === 'edit') {
                onEdit(tileId);
              } else if (opt.key === 'remove') {
                void onRemove(tileId);
              }
            }}
            isFluid={false}
            testId={`byod-tile-actions-${tileId}`}
          />
        </span>
      );
    }, [canEdit, isRemoving, tileId, onEdit, onRemove, t, language]);

    if (fetchStatus === 'idle' && !data && !isError) {
      return (
        <div
          ref={ref}
          className={classify(vizCss.tile, vizCss.tileChartWrapperDiv)}
          data-tile-id={tileId}
          data-testid={`byod-chart-tile-${tileId}-skeleton`}
        />
      );
    }

    const isChartData = data?.widget_type === WIDGET_TYPE.CHART;
    const chartConfig = isChartData ? data.chart_config : null;
    const chartData = isChartData ? data.data : [];

    if (isError) {
      return (
        <div ref={ref} className={vizCss.tileChart} data-tile-id={tileId}>
          <ChartWrapper
            title={label}
            isLoading={isFetching}
            headerActions={menuActions}
            hasEmptyData
            emptyText={
              <EmptyState
                title={t('Failed_to_load_report', 'Failed to load report')}
                description={error?.message}
                imageVariant="chart"
              >
                <Button
                  type="secondary"
                  size="small"
                  iconLeftName="arrow-rotate-right"
                  iconLeftType="regular"
                  onClick={refetch}
                  testId={`byod-chart-tile-${tileId}-retry`}
                >
                  {t('Retry', 'Retry')}
                </Button>
              </EmptyState>
            }
            classNames={{wrapper: vizCss.tileChartWrapper}}
            testId={`byod-chart-tile-${tileId}`}
          />
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={vizCss.tileChartWrapperDiv}
        data-tile-id={tileId}
      >
        <ByodChart
          label={label}
          chartConfig={chartConfig}
          data={chartData}
          chartStyle={chartStyle}
          onChartStyleChange={canEdit ? handleChartStyleChange : undefined}
          headerActions={menuActions}
          chartHeight={CHART_TILE_HEIGHT}
          isLoading={!data || isFetching}
          classNames={{
            container: vizCss.tileChart,
            wrapper: vizCss.tileChartWrapper,
            content: vizCss.tileChartContent,
            highChart: vizCss.tileChartHighChart,
          }}
          testId={`byod-chart-tile-${tileId}`}
        />
      </div>
    );
  },
);
